import { Injectable } from '@angular/core';
import { CrossProduct } from '../models/cross-product/cross-product.model';
import { IProductSlotName } from '../interfaces/additional-products.interface';

/**
 * Represents the data transfer object for cross-product submission preferences.
 * This interface defines the selection of additional services or consents
 * within a multi-product submission.
 *
 * Properties:
 * - `insuranceIncluded` - Indicates whether an insurance service is included.
 * - `consultationIncluded` - User's consent for legal consultation.
 * - `vetPetIncluded` - Indicates whether veterinary or pet-related services are included.
 * - `dietPlanIncluded` - Indicates whether a diet plan service is included.
 * - `astrologerConsultingIncluded` - Indicates whether an astrology-related service is included.
 * - `psychologistConsultingIncluded` - Indicates whether a psychologist consultation is included.
 * - `dietConsultingIncluded` - Indicates whether diet consulting services are included.
 * - `elderIncluded` - Indicates whether elder-related services are included.
 * - `finHelperIncluded` - Indicates whether financial helper service is included.
 * - `socialGidIncluded` - Indicates whether social guide service is included.
 * - `loanTransferConsent` - User's consent for loan transfer processing.
 */
export interface CrossProductSubmissionDto {
  insuranceIncluded: boolean;
  consultationIncluded: boolean;
  vetPetIncluded: boolean;
  dietPlanIncluded: boolean;
  astrologerConsultingIncluded: boolean;
  psychologistConsultingIncluded: boolean;
  dietConsultingIncluded: boolean;
  elderIncluded: boolean;
  loanTransferConsent: boolean;
  finHelperIncluded: boolean;
  socialGidIncluded: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CrossProductSubmissionService {
  /**
   * Преобразует массив кросс-продуктов в объект DTO для отправки на сервер
   * @param crossList массив кросс-продуктов
   * @param loanTransferConsent согласие на передачу займа
   */
  prepareCrossProductDto(
    crossList: CrossProduct[],
    loanTransferConsent: boolean
  ): CrossProductSubmissionDto {
    const dto: CrossProductSubmissionDto = {
      insuranceIncluded: false,
      consultationIncluded: false,
      vetPetIncluded: false,
      dietPlanIncluded: false,
      astrologerConsultingIncluded: false,
      psychologistConsultingIncluded: false,
      dietConsultingIncluded: false,
      elderIncluded: false,
      finHelperIncluded: false,
      socialGidIncluded: false,
      loanTransferConsent,
    };

    crossList.forEach((cross) => {
      // Проверяем, что кросс выбран, т.е. model = true
      if (!cross.model) {
        return;
      }

      switch (cross.name) {
        case IProductSlotName.INSURANCE:
          dto.insuranceIncluded = true;
          break;
        case IProductSlotName.LAWYER_CONSULTING:
          dto.consultationIncluded = true;
          break;
        case IProductSlotName.VETPET_CONSULTING:
          dto.vetPetIncluded = true;
          break;
        case IProductSlotName.DIET_PLAN_FAVOR:
          dto.dietPlanIncluded = true;
          break;
        case IProductSlotName.ASTROLOGER_CONSULTING:
          dto.astrologerConsultingIncluded = true;
          break;
        case IProductSlotName.PSYCHOLOGIST_CONSULTING:
          dto.psychologistConsultingIncluded = true;
          break;
        case IProductSlotName.DIET_CONSULTING:
          dto.dietConsultingIncluded = true;
          break;
        case IProductSlotName.ELDER:
          dto.elderIncluded = true;
          break;
        case IProductSlotName.FIN_HELPER:
          dto.finHelperIncluded = true;
          break;
        case IProductSlotName.SOCIAL_GID:
          dto.socialGidIncluded = true;
          break;
      }
    });

    return dto;
  }

  /**
   * Преобразует массив кросс-продуктов в объект с флагами для методов sendConfirm и sendConfirmWithRecurring
   * @param crossList массив кросс-продуктов
   * @returns объект с флагами для каждого типа кросс-продукта
   */
  prepareCrossProductFlags(crossList: CrossProduct[]): Record<string, boolean> {
    const result = {
      insuranceIncluded: false,
      consultationIncluded: false,
      vetPetIncluded: false,
      dietPlanIncluded: false,
      astrologerConsultingIncluded: false,
      psychologistConsultingIncluded: false,
      dietConsultingIncluded: false,
      elderIncluded: false,
      finHelperIncluded: false,
      socialGidIncluded: false,
    };

    crossList.forEach((cross) => {
      // Важно: проверяем model как признак выбора кросс-продукта
      if (!cross.model) {
        return;
      }

      switch (cross.name) {
        case IProductSlotName.INSURANCE:
          result.insuranceIncluded = true;
          break;
        case IProductSlotName.LAWYER_CONSULTING:
          result.consultationIncluded = true;
          break;
        case IProductSlotName.VETPET_CONSULTING:
          result.vetPetIncluded = true;
          break;
        case IProductSlotName.DIET_PLAN_FAVOR:
          result.dietPlanIncluded = true;
          break;
        case IProductSlotName.ASTROLOGER_CONSULTING:
          result.astrologerConsultingIncluded = true;
          break;
        case IProductSlotName.PSYCHOLOGIST_CONSULTING:
          result.psychologistConsultingIncluded = true;
          break;
        case IProductSlotName.DIET_CONSULTING:
          result.dietConsultingIncluded = true;
          break;
        case IProductSlotName.ELDER:
          result.elderIncluded = true;
          break;
        case IProductSlotName.FIN_HELPER:
          result.finHelperIncluded = true;
          break;
        case IProductSlotName.SOCIAL_GID:
          result.socialGidIncluded = true;
          break;
      }
    });

    return result;
  }

  /**
   * Рассчитывает общую стоимость выбранных кросс-продуктов
   * @param crossList массив кросс-продуктов
   * @returns сумма стоимости всех выбранных кросс-продуктов
   */
  calculateTotalCrossProductAmount(crossList: CrossProduct[]): number {
    const uniqueCrosses = new Map<string, CrossProduct>();

    crossList
      .filter((cross) => cross.model)
      .forEach((cross) => {
        if (!uniqueCrosses.has(cross.name)) {
          uniqueCrosses.set(cross.name, cross);
        }
      });

    return Array.from(uniqueCrosses.values()).reduce(
      (total, cross) => total + (cross.info?.purchaseAmount || 0),
      0
    );
  }
}
